/**
 * Vanilla JavaScript Calculator
 * Uses HTML from PHP and CSS classes for visibility
 */

class Calculator {
	constructor() {
		// State management
		this.state = {
			meters: '',
			interiorCorners: '',
			exteriorCorners: '',
			result: false,
			errors: [],
			email: '',
			mailSent: false,
		};

		// Language configuration
		this.lang = document.documentElement.lang;
		this.imperial = this.lang === 'en-US';

		// Get config from global
		this.config = window.dekodeFrontend?.calculator || {};
		this.str = this.config.strings || {};

		// Get DOM references
		this.modal = document.getElementById('calculator-modal');
		this.form = this.modal.querySelector('[data-form="calculator"]');
		this.resultContainer = this.modal.querySelector('[data-result]');
		this.submitBtn = this.modal.querySelector('[data-submit-btn]');

		// Bind methods
		this.handleNumberChange = this.handleNumberChange.bind(this);
		this.handleEmailChange = this.handleEmailChange.bind(this);
		this.calculate = this.calculate.bind(this);
		this.sendMail = this.sendMail.bind(this);
		this.closeModal = this.closeModal.bind(this);
		this.reset = this.reset.bind(this);

		// Initialize
		this.attachEventListeners();
		this.setupConditionalItems();
	}

	setupConditionalItems() {
		// Show/hide imperial items
		if (this.imperial) {
			const imperialItem = this.modal.querySelector('.imperial-only');
			if (imperialItem) {
				imperialItem.classList.remove('hidden');
			}
		}

		// Show/hide click wax
		if (this.str.clickWax) {
			const clickWaxItem = this.modal.querySelector('.clickwax-item');
			if (clickWaxItem) {
				clickWaxItem.classList.remove('hidden');
			}
		}

		// Setup links
		if (this.config.installationPageUrl) {
			const link = this.modal.querySelector('[data-link="installation"]');
			if (link) {
				link.href = this.config.installationPageUrl;
				link.textContent = this.str.installationLinkText;
				link.classList.remove('hidden');
			}
		}

		if (this.config.locationsPageUrl) {
			const wrapper = this.modal.querySelector('[data-link-wrapper="locations"]');
			const link = this.modal.querySelector('[data-link="locations"]');
			if (link && wrapper) {
				link.href = this.config.locationsPageUrl;
				link.textContent = this.str.findLocations;
				wrapper.classList.remove('hidden');
			}
		}

		// Show mail form if enabled
		if (this.config.mailEnabled) {
			const mailForm = this.modal.querySelector('[data-form="mail"]');
			if (mailForm) {
				mailForm.classList.remove('hidden');
			}
		}
	}

	show() {
		this.modal.classList.remove('hidden');
		this.modal.setAttribute('aria-hidden', 'false');
		window.setTimeout(() => {
			this.modal.classList.remove('waiting');
		}, 150);
	}

	handleNumberChange(name, value) {
		// Remove error if field now has value
		if (this.state.errors.includes(name) && value !== '') {
			this.state.errors = this.state.errors.filter((err) => err !== name);
		}

		// Update state
		this.state[name] = value.toString().replace(',', '.');

		// Re-calculate if we already have results
		if (this.state.result) {
			const meters = name === 'meters' ? parseFloat(value.toString().replace(',', '.')) : null;
			const inner = name === 'interiorCorners' ? value : this.state.interiorCorners;
			const outer = name === 'exteriorCorners' ? value : this.state.exteriorCorners;
			this.calculate(meters, inner, outer);
		} else {
			// Update UI for errors
			this.updateInputError(name);
		}
	}

	updateInputError(name) {
		const input = this.modal.querySelector(`[data-input="${name}"]`);
		const error = this.modal.querySelector(`[data-error="${name}"]`);
		const invalid = this.state.errors.includes(name);

		if (input) {
			input.setAttribute('aria-invalid', invalid);
		}
		if (error) {
			error.textContent = invalid ? this.str.required : '';
		}
	}

	calculate(value, inner, outer) {
		const length = value ?? this.state.meters;
		const innerCorners = parseFloat(inner ?? this.state.interiorCorners) || 0;
		const outerCorners = parseFloat(outer ?? this.state.exteriorCorners) || 0;

		if ('' === length) {
			this.state.errors = ['meters'];
			this.updateInputError('meters');
			return;
		}

		const result = {};
		const formula = this.imperial ? length / 23 : length * 1.75;

		if (formula) {
			result.numberOfBoard = parseFloat(formula);
			result.packages = Math.ceil(result.numberOfBoard / 2);
			result.screws = Math.ceil(result.packages / 5);
			result.sealant = Math.ceil(result.packages / 3);

			result.sealant = result.sealant + Math.ceil((innerCorners + outerCorners) / 2);

			result.baseProfiles = Math.ceil(result.numberOfBoard * 0.27);
			result.fiboClean = Math.ceil(result.numberOfBoard * 0.07);
			result.fiboWipes = Math.ceil(result.numberOfBoard * 0.07);
			result.innerCornerProfiles = innerCorners;
			result.outerCornerProfiles = outerCorners;
			result.lProfiles = Math.ceil(result.baseProfiles + 2);
			result.kolss = Math.ceil(result.packages / result.packages);
			result.clickWax = 1;

			this.state.result = result;
			this.state.errors = [];
			this.state.mailSent = false;

			this.showResults();
		} else {
			this.state.errors = ['meters'];
			this.updateInputError('meters');
		}
	}

	showResults() {
		// Hide submit button, show results
		this.submitBtn.classList.add('hidden');
		this.resultContainer.classList.remove('hidden');

		// Update all result values
		this.updateValue('packages', this.state.result.packages);
		this.updateValue('screws', this.state.result.screws);
		this.updateValue('sealant', this.state.result.sealant);
		this.updateValue('baseProfiles', this.state.result.baseProfiles);
		this.updateValue('innerCornerProfiles', this.state.interiorCorners || 0);
		this.updateValue('outerCornerProfiles', this.state.exteriorCorners || 0);
		this.updateValue('fiboClean', this.state.result.fiboClean);
		this.updateValue('fiboWipes', this.state.result.fiboWipes);
		this.updateValue('numSealing', this.state.result.kolss);
		this.updateValue('numTapping', this.state.result.kolss);
		this.updateValue('numGrip', this.state.result.kolss);

		if (this.imperial) {
			this.updateValue('lProfiles', this.state.result.lProfiles);
		}

		if (this.str.clickWax) {
			this.updateValue('clickWax', this.state.result.clickWax);
		}
	}

	updateValue(key, value) {
		const el = this.modal.querySelector(`[data-value="${key}"]`);
		if (el) {
			el.textContent = value;
		}
	}

	closeModal(e) {
		if (e) {
			e.preventDefault();
		}
		window.closeCalculator();
	}

	sendMail(e) {
		if (e) {
			e.preventDefault();
		}

		this.state.mailSent = false;
		this.updateMailButton();

		fetch(`/wp-json/calculator/v2/post`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				email: this.state.email,
				meters: this.state.meters,
				imperial: this.imperial,
				result: this.state.result,
				install: this.config.installationPageUrl,
				find: this.config.locationsPageUrl,
				lang: this.config.lang,
			}),
		})
			.then((response) => response.json())
			.then((data) => {
				if (data) {
					this.state.mailSent = true;
					this.updateMailButton();
				}
			});
	}

	updateMailButton() {
		const btn = this.modal.querySelector('[data-mail-submit]');
		if (btn) {
			btn.value = this.state.mailSent ? this.str.mailSent : this.str.mailSend;
		}
	}

	handleEmailChange(e) {
		this.state.email = e.target.value;
	}

	reset(e) {
		if (e) {
			e.preventDefault();
		}

		this.state.result = false;
		this.resultContainer.classList.add('hidden');
		this.submitBtn.classList.remove('hidden');

		// Clear errors
		this.state.errors = [];
		['meters', 'interiorCorners', 'exteriorCorners'].forEach((name) => {
			this.updateInputError(name);
		});
	}

	attachEventListeners() {
		// Close button
		const closeBtn = this.modal.querySelector('[data-action="close"]');
		if (closeBtn) {
			closeBtn.addEventListener('click', this.closeModal);
		}

		// Form submit
		if (this.form) {
			this.form.addEventListener('submit', (e) => {
				e.preventDefault();
				this.calculate();
			});
		}

		// Text inputs
		const textInputs = this.modal.querySelectorAll('input[type="text"]');
		textInputs.forEach((input) => {
			input.addEventListener('input', (e) => {
				const name = e.target.getAttribute('data-input');
				const value = e.target.value;
				if (!Number.isInteger(value)) {
					this.handleNumberChange(name, value);
				}
			});
		});

		// Increase/Decrease buttons
		const arrowButtons = this.modal.querySelectorAll('.input-arrow');
		arrowButtons.forEach((button) => {
			button.addEventListener('click', (e) => {
				e.preventDefault();
				const action = e.currentTarget.getAttribute('data-action');
				const inputName = e.currentTarget.getAttribute('data-input');
				let currentValue = parseInt(this.state[inputName]) || 0;

				if (action === 'increase') {
					currentValue = currentValue + 1;
					if (currentValue <= 0 || isNaN(currentValue)) {
						currentValue = 1;
					}
				} else if (action === 'decrease') {
					currentValue = currentValue - 1;
					if (currentValue < 0 || isNaN(currentValue)) {
						currentValue = 0;
					}
				}

				this.handleNumberChange(inputName, currentValue);

				// Update the input display
				const input = this.modal.querySelector(`[data-input="${inputName}"]`);
				if (input) {
					input.value = currentValue;
				}
			});
		});

		// Reset button
		const resetBtn = this.modal.querySelector('[data-action="reset"]');
		if (resetBtn) {
			resetBtn.addEventListener('click', this.reset);
		}

		// Email input
		const emailInput = this.modal.querySelector('[data-input="email"]');
		if (emailInput) {
			emailInput.addEventListener('input', this.handleEmailChange);
		}

		// Mail form submit
		const mailForm = this.modal.querySelector('[data-form="mail"]');
		if (mailForm) {
			mailForm.addEventListener('submit', this.sendMail);
		}
	}
}

export default Calculator;
